import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import type { TypedAnalyticsContexts } from './analytics-context.types';
import { useAnalyticsFeatureFlags } from './hooks/useAnalyticsFeatureFlags';

/**
 * AnalyticsTrackingProvider lets you inject or update analytics context (e.g., consultation, feature flags)
 * for use with `useEmitUiEvent` and other Snowplow-based analytics logic.
 *
 * You pass an `update` function, which receives the current tracking context and returns a new one.
 * This enables layered tracking — for example, setting a `section_id` for a specific screen.
 *
 * ⚠️ Note:
 * - This provider does **not** enforce merging or protect against accidental overwrites.
 *   If you want to preserve existing context values, you must explicitly merge them in your update function.
 * - To avoid unnecessary recalculations on re-render, **wrap your `update` function in `useCallback`**.
 *   This ensures the context value is only recomputed when necessary.
 *
 * Example:
 * ```tsx
 * const updateTrackingContext = useCallback(
 *   (ctx) => ({
 *     ...ctx,
 *     consultation: {
 *       ...ctx.consultation,
 *       consultation_id: '123'
 *     }
 *   }),
 *   []
 * );
 *
 * <AnalyticsTrackingProvider update={updateTrackingContext}>
 *   <ConsultNotes />
 * </AnalyticsTrackingProvider>
 * ```
 */

const AnalyticsTrackingContext = createContext<TypedAnalyticsContexts | null>(null);

interface AnalyticsTrackingProviderProps {
  children: ReactNode;
  update?: (current: TypedAnalyticsContexts) => TypedAnalyticsContexts;
}

export const AnalyticsTrackingProvider = ({ children, update }: AnalyticsTrackingProviderProps) => {
  const context = useContext(AnalyticsTrackingContext);
  const analyticsFlags = useAnalyticsFeatureFlags();

  const mergedValue = useMemo<TypedAnalyticsContexts>(() => {
    const currentContext = context ?? {};
    const updated = update ? update(currentContext) : currentContext;

    return {
      ...updated,
      flags: { flags: analyticsFlags },
    };
  }, [context, update, analyticsFlags]);

  return <AnalyticsTrackingContext.Provider value={mergedValue}>{children}</AnalyticsTrackingContext.Provider>;
};

/**
 * Returns the current analytics context from AnalyticsTrackingProvider.
 * If no provider is present, returns an empty object instead of throwing.
 *
 * This allows components to use `useEmitUiEvent` safely outside of the provider,
 * which is important for shared components and components that do not require context enrichment.
 *
 * Note: This fallback is intentional — throwing here would break UI tracking in components
 * that don't need or expect analytics context (e.g. buttons used across multiple contexts).
 */

export const useAnalyticsTracking = () => {
  return useContext(AnalyticsTrackingContext) ?? {};
};
